import React, { useEffect, useRef, useState } from "react";
import { View, Text, Animated, StyleSheet, Platform, Dimensions } from "react-native";
import { colors, textStyle } from "../styles";
import { TOAST_DURATION } from "./toastDurations";

export type ToastVariant = "light" | "dark" | "purple";

interface ToastProps {
  title?: string;
  body?: string;
  icon?: React.ReactNode;
  variant?: ToastVariant;
  visible: boolean;
  duration?: number;
  onDismiss?: () => void;
}

const THEME = {
  light: {
    bg: colors.toastLightBg,
    title: colors.toastLightText,
    body: colors.toastLightText,
  },
  dark: {
    bg: colors.toastDarkBg,
    title: colors.toastDarkTitle,
    body: colors.toastDarkBody,
  },
  purple: {
    bg: colors.toastPurpleBg,
    title: colors.white,
    body: colors.white,
  },
} as const;

export default function Toast({ title, body, icon, variant = "dark", visible, duration = TOAST_DURATION.SHORT, onDismiss }: ToastProps) {
  const translateY = useRef(new Animated.Value(100)).current;
  const opacity = useRef(new Animated.Value(0)).current;
  const [mounted, setMounted] = useState(visible);
  const hasBeenVisible = useRef(visible);

  useEffect(() => {
    if (visible) {
      hasBeenVisible.current = true;
      setMounted(true);
      Animated.parallel([
        Animated.spring(translateY, { toValue: 0, useNativeDriver: true, friction: 8, tension: 40 }),
        Animated.timing(opacity, { toValue: 1, duration: 200, useNativeDriver: true }),
      ]).start();

      if (duration > 0) {
        const timer = setTimeout(() => {
          dismiss();
        }, duration);
        return () => clearTimeout(timer);
      }
    } else if (hasBeenVisible.current) {
      dismiss();
    }
  }, [visible]);

  const dismiss = () => {
    Animated.parallel([
      Animated.timing(translateY, { toValue: 100, duration: 250, useNativeDriver: true }),
      Animated.timing(opacity, { toValue: 0, duration: 250, useNativeDriver: true }),
    ]).start(({ finished }) => {
      if (finished) {
        setMounted(false);
        onDismiss?.();
      }
    });
  };

  const theme = THEME[variant];

  if (!mounted || (!title && !body)) return null;

  return (
    <Animated.View style={[styles.container, { transform: [{ translateY }], opacity }]}>
      <View style={[styles.toast, { backgroundColor: theme.bg }]}>
        {icon && <View style={styles.icon}>{icon}</View>}
        <View style={styles.content}>
          {title ? (
            <>
              <Text style={[styles.title, { color: theme.title }]}>{title}</Text>
              {body ? <Text style={[styles.body, { color: theme.body }]}>{body}</Text> : null}
            </>
          ) : body ? (
            <Text style={[styles.bodyOnly, { color: theme.body }]}>{body}</Text>
          ) : null}
        </View>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    bottom: Platform.OS === "web" ? 32 : 48,
    left: 0,
    right: 0,
    alignItems: "center",
    zIndex: 9999,
    pointerEvents: "none",
  },
  toast: {
    flexDirection: "row",
    borderRadius: 36,
    paddingLeft: 16,
    paddingRight: 24,
    paddingVertical: 16,
    minHeight: 80,
    width: 448,
    maxWidth: Dimensions.get("window").width - 32, // never overflow narrow screens

    shadowColor: "#000",
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.2,
    shadowRadius: 20,
    elevation: 8,
  },
  icon: {
    marginLeft: 8,
    marginRight: 16,
    justifyContent: "center" as const,
  },
  content: {
    flex: 1,
    justifyContent: "center" as const,
    gap: 2,
  },
  title: {
    fontSize: 20,
    ...textStyle.headingMedium,
    lineHeight: 24,
  },
  body: {
    fontSize: 16,
    ...textStyle.body,
    lineHeight: 24,
  },
  bodyOnly: {
    fontSize: 16,
    ...textStyle.bodyMedium,
    lineHeight: 24,
  },
});
